import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged, ValueOf } from 'type-fest';
import { MutableEntity } from '../../common';
import type { CommandContext } from '../../common/context';
import { UniqueShortId } from '../../common/id';
import { ImportTableDefinitionRevision } from '../importTableDefinitionRevision';
import { SourceSystem } from '../sourceSystem';
import { _VerifyPayload } from '../verifyPayload';
import {
  advanceRevisionPointers as _advanceRevisionPointers,
  resolveRevisionPointersAfterCreate as _resolveRevisionPointersAfterCreate,
} from './revisionPointers';
import { _SchemaJson } from './schema';
import { _UniqueKeys } from './uniqueKeys';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(12);

const _インポート経路 = {
  Upload: 'Upload',
  Collector: 'Collector',
} as const;
type _インポート経路 = ValueOf<typeof _インポート経路>;

const _LoadMode = {
  Upsert: 'Upsert',
  Replace: 'Replace',
} as const;
type _LoadMode = ValueOf<typeof _LoadMode>;

/**
 * インポートテーブル定義（メタデータと revision ポインタ）
 *
 * スキーマ・取り込み設定は {@link ImportTableDefinitionRevision} が正本。
 */
export type ImportTableDefinition = MutableEntity & {
  projectId: Project.Id;
  id: ImportTableDefinition.Id;
  sourceSystemId: SourceSystem.Id;

  /** 表示名 */
  displayName: string;
  description: string | undefined;

  isActive: boolean;

  /** 顧客・契約データとして現在有効な revision ID。初回 import 成功前は未設定。 */
  activeRevisionId: ImportTableDefinitionRevision.Id | undefined;

  /** pending / failed を含む最新作成済み revision ID */
  lastRevisionId: ImportTableDefinitionRevision.Id;
};

export namespace ImportTableDefinition {
  export import SchemaJson = _SchemaJson;

  export type Id = Tagged<UniqueShortId, 'importTableDefinitionId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export type インポート経路 = _インポート経路;
  export namespace インポート経路 {
    export const FromString = (s: string): インポート経路 => {
      const value = _インポート経路[s as keyof typeof _インポート経路];
      if (!value) throw new Error(`Invalid ImportTableDefinition.ImportMethod: ${s}`);
      return value;
    };
  }

  export type LoadMode = _LoadMode;
  export namespace LoadMode {
    export const FromString = (s: string): LoadMode => {
      const value = _LoadMode[s as keyof typeof _LoadMode];
      if (!value) throw new Error(`Invalid ImportTableDefinition.LoadMode: ${s}`);
      return value;
    };
  }

  /** 一意キー設定 */
  export import UniqueKeys = _UniqueKeys;

  /** Customer DB verify 用 payload キー解決 */
  export import VerifyPayload = _VerifyPayload;

  export const resolveRevisionPointersAfterCreate = _resolveRevisionPointersAfterCreate;
  export const advanceRevisionPointers = _advanceRevisionPointers;

  export type Props = {
    id: string;
    projectId: string;
    sourceSystemId: string;
    displayName: string;
    description: string | undefined;
    isActive: boolean;
    activeRevisionId: string | undefined;
    lastRevisionId: string;
  } & MutableEntity.Props;
  export const Construct = (props: Props): ImportTableDefinition => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      sourceSystemId: SourceSystem.Id.FromString(props.sourceSystemId),
      displayName: props.displayName,
      description: props.description,
      isActive: props.isActive,
      activeRevisionId: props.activeRevisionId ? ImportTableDefinitionRevision.Id.FromString(props.activeRevisionId) : undefined,
      lastRevisionId: ImportTableDefinitionRevision.Id.FromString(props.lastRevisionId),
      ...MutableEntity.Construct(props),
    };
  };

  /** API 詳細・一覧など definition と revision を同時に返すとき用 */
  export type Detail = {
    definition: ImportTableDefinition;
    activeRevision: ImportTableDefinitionRevision | undefined;
    lastRevision: ImportTableDefinitionRevision;
  };

  /** import 実行時にメタデータと参照中 revision を束ねる内部型 */
  export type Resolved = {
    definition: ImportTableDefinition;
    revision: ImportTableDefinitionRevision;
  };

  export namespace Create {
    export type Props = Pick<
      ImportTableDefinition.Props,
      | 'sourceSystemId' //
      | 'displayName'
      | 'description'
      | 'isActive'
      | 'activeRevisionId'
      | 'lastRevisionId'
    >;
    export type Params = {
      context: CommandContext;
      props: Props;
    };
  }
  export const Create = (params: Create.Params): ImportTableDefinition => {
    const { context, props } = params;
    return Construct({
      id: Id.Generate(),
      projectId: context.projectId,
      sourceSystemId: props.sourceSystemId,
      displayName: props.displayName,
      description: props.description,
      isActive: props.isActive,
      activeRevisionId: props.activeRevisionId,
      lastRevisionId: props.lastRevisionId,
      ...MutableEntity.Create(context),
    });
  };

  export namespace Update {
    export type Props = Partial<
      Pick<
        ImportTableDefinition.Props, //
        'displayName' | 'description' | 'isActive'
      >
    >;
    export type Params = {
      current: ImportTableDefinition;
      props: Props;
      context: CommandContext;
    };
  }

  export const Update = (params: Update.Params): ImportTableDefinition => {
    const { current, props, context } = params;
    return {
      ...current,
      displayName: props.displayName ?? current.displayName,
      description: props.description !== undefined ? props.description : current.description,
      isActive: props.isActive ?? current.isActive,
      ...MutableEntity.Update({ cur: current, ...context }),
    };
  };
}
